import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
// import { PayPalScriptProvider } from "@paypal/react-paypal-js"; // Commented out for now
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import OurStory from './pages/OurStory';
import Services from './pages/Services';
import Donate from './pages/Donate';
import Apply from './pages/Apply';
import LeadershipPage from './pages/Leadership';
import ProtectedRoute from './components/ProtectedRoute';
import Portal from './pages/Portal';
import PortalLogin from './pages/PortalLogin';
import ExploreCauses from './pages/ExploreCauses';
import AdminLayout from './layouts/AdminLayout';
import ApplicationAnalytics from './components/Analytics/ApplicationAnalytics';
import Dashboard from './components/Dashboard';
import ApplicationReview from './components/ApplicationReview';
import ImpactAcademy from './pages/ImpactAcademy';
import GeneralDonate from './pages/GeneralDonate';
import AnnualReport from './pages/AnnualReport';
import CostCalculatorPage from './pages/CostCalculatorPage';
import GoogleAnalytics from './components/GoogleAnalytics';
import { trackPageView } from './utils/analytics';
import ProjectDashboard from './pages/ProjectDashboard';
import { BudgetProvider } from './contexts/BudgetContext';
import { AuthProvider } from './contexts/AuthContext';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import '@fortawesome/fontawesome-svg-core/styles.css';
import 'react-datepicker/dist/react-datepicker.css';
import { Toaster } from 'react-hot-toast';
import { DashboardLayout } from './sponsfi/components/Dashboard/DashboardLayout.tsx';
import { DashboardOverview } from './sponsfi/components/Dashboard/Overview';
import { AccountingDashboard } from './sponsfi/components/Accounting/AccountingDashboard';
import { PaymentsDashboard } from './sponsfi/components/Payments/PaymentsDashboard.tsx';
import { DocumentsDashboard } from './sponsfi/components/Documents/DocumentsDashboard.tsx';
import { ReportsDashboard } from './sponsfi/components/Reports/ReportsDashboard.tsx';
import { SettingsDashboard } from './sponsfi/components/Settings/SettingsDashboard.tsx';
import { BudgetOverview } from './components/Dashboard/BudgetOverview/BudgetOverview';
import SponsFIApp from './sponsfi/SponsFIApp';
import Analytics from './pages/admin/Analytics';
import { ServiceProvider } from './sponsfi/contexts/ServiceProvider';
import { Reports } from './sponsfi/components/Reports';
import { SystemHealthCheck } from './sponsfi/components/SystemHealthCheck';
import SmartInbox from './components/messaging/SmartInbox';
import Cases from './components/messaging/Cases';
import Tasks from './components/messaging/Tasks';
import MessageArchive from './components/messaging/MessageArchive';
import { SmartComplyDashboard } from './components/SmartComply/SmartComplyDashboard';
import TermsOfService from './pages/TOS.jsx';
import PrivacyPolicy from './pages/PrivacyPolicy.jsx';
// Add Font Awesome icons to the library
library.add(fas, far, fab);

function App() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname);
  }, [location]);

  // Commented out PayPal for now
  // const paypalOptions = {
  //   "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
  //   currency: "USD",
  //   intent: "capture",
  // };

  return (
    <AuthProvider>
      <BudgetProvider>
        <ServiceProvider>
          <GoogleAnalytics />
          <Header />
          <main>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/our-story" element={<OurStory />} />
              <Route path="/leadership" element={<LeadershipPage />} />
              <Route path="/services" element={<Services />} />
              <Route path="/causes" element={<ExploreCauses />} />
              <Route path="/donate" element={<GeneralDonate />} />
              <Route path="/donate/:causeId" element={<Donate />} />
              <Route path="/apply" element={<Apply />} />
              <Route path="/academy" element={<ImpactAcademy />} />
              <Route path="/calculator" element={<CostCalculatorPage />} />
              

               {/* Project Dashboard - temporarily public for development */}
               <Route path="/dashboard/cause/:causeId" element={<ProjectDashboard />} />
            
              {/* Portal Routes */}
              <Route path="/portal" element={<PortalLogin />} />
              
              {/* Protected Portal Routes */}
              <Route
                path="/portal/cause/*"
                element={
                  <ProtectedRoute>
                    <Portal />
                  </ProtectedRoute>
                }
              />

              {/* Protected Admin Routes */}
              <Route
                path="/portal/admin/*"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="review-applications" element={<ApplicationReview />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="accounting" element={<AccountingDashboard />} />
                <Route path="system-health" element={<SystemHealthCheck />} />
                
                {/* Add Smart Comply Route */}
                <Route path="smart-comply" element={<SmartComplyDashboard />} />
                
                {/* Messages Routes */}
                <Route path="messages">
                  <Route path="inbox" element={<SmartInbox />} />
                  <Route path="cases" element={<Cases />} />
                  <Route path="tasks" element={<Tasks />} />
                  <Route path="archive" element={<MessageArchive />} />
                </Route>

                <Route path="sponsfi">
                  <Route index element={<DashboardOverview fiscalSponsorId="default" />} />
                  <Route path="overview" element={<DashboardOverview fiscalSponsorId="default" />} />
                  <Route path="accounting" element={<AccountingDashboard />} />
                </Route>
              </Route>

              <Route path="/cost-calculator" element={<CostCalculatorPage />} />
              <Route path="/financials" element={<AnnualReport />} />

              {/* SponsFI routes */}
              <Route path="/sponsfi" element={<DashboardLayout />}>
                <Route index element={<DashboardOverview />} />
                <Route path="accounting" element={<AccountingDashboard />} />
                <Route path="payments" element={<PaymentsDashboard />} />
                <Route path="documents" element={<DocumentsDashboard />} />
                <Route path="reports" element={<ReportsDashboard />} />
                <Route path="settings" element={<SettingsDashboard />} />
              </Route>

              {/* Add SponsFI routes */}
              <Route path="/sponsfi/*" element={<SponsFIApp />} />

              <Route path="/portal/admin/sponsfi/reports" element={<Reports />} />

              {/* Add Terms and Privacy routes */}
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
            </Routes>
          </main>
          <Footer />
          <Toaster position="top-right" />
        </ServiceProvider>
      </BudgetProvider>
    </AuthProvider>
    // </PayPalScriptProvider>
  );
}

export default App;
